/**
 * A `chart` block's numbers (slice 2g spec §8.2, boards 53 and 53b): its
 * values formatted by `unit`, a bar chart's scale from zero, and a line
 * chart's points. Pure, so it's tested on its own (`chart.test.ts`).
 */

/** A unit that's one currency symbol goes before the number. */
const CURRENCY = /^\p{Sc}$/u;

/**
 * `value` with its unit: a currency symbol before ("$1,615.87", whole
 * amounts without cents: "$700"), `%` after with no space ("12%"), any other
 * after a space ("4.5 km").
 */
export function formatValue(value: number, unit: string): string {
  const money = CURRENCY.test(unit);
  const digits = money && !Number.isInteger(value) ? 2 : 0;
  const n = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: money ? digits : 2,
  });
  const sign = value < 0 ? "-" : "";
  if (money) return `${sign}${unit}${n}`;
  if (unit === "%") return `${sign}${n}%`;
  return `${sign}${n} ${unit}`;
}

export type Bar = {
  /** Where the bar starts and how long it is, as fractions of the track. */
  start: number;
  length: number;
  negative: boolean;
};

/**
 * A bar chart's bars on one scale, from the smaller of 0 and the least value
 * to the larger of 0 and the greatest; `zero` is where 0 sits on the track
 * (0 unless a value is negative). Every value 0: no bars.
 */
export function bars(values: readonly number[]): { zero: number; bars: Bar[] } {
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const span = hi - lo;
  const zero = span === 0 ? 0 : -lo / span;
  return {
    zero,
    bars: values.map((v) => ({
      start: span === 0 ? 0 : (Math.min(v, 0) - lo) / span,
      length: span === 0 ? 0 : Math.abs(v) / span,
      negative: v < 0,
    })),
  };
}

/** The line chart's drawing box (board 53): 150 tall, the axis at 140, points between 38 and 110. */
export const LINE = { height: 150, axis: 140, top: 38, bottom: 110, inset: 30 } as const;

/**
 * A line chart's points across `width`, from the least value (at the bottom)
 * to the greatest (at the top), as board 53 draws it. `zero` is the y of a
 * zero gridline when the values cross 0, else null. When every value is the
 * same the line is flat: on the axis when they're all 0, else midway.
 */
export function linePoints(
  values: readonly number[],
  width: number,
): { points: { x: number; y: number }[]; zero: number | null } {
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const n = values.length;
  const step = n > 1 ? (width - 2 * LINE.inset) / (n - 1) : 0;
  const x = (i: number) => (n > 1 ? LINE.inset + i * step : width / 2);
  const y = (v: number) => {
    if (hi === lo) return v === 0 ? LINE.axis : (LINE.top + LINE.bottom) / 2;
    return LINE.bottom - ((v - lo) / (hi - lo)) * (LINE.bottom - LINE.top);
  };
  return {
    points: values.map((v, i) => ({ x: x(i), y: y(v) })),
    zero: lo < 0 && hi > 0 ? y(0) : null,
  };
}

/** Past this many points, a line chart on a phone keeps the wide spacing and scrolls. */
export const LINE_PHONE_POINTS = 6;
